(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const setActiveImage = (images, key, keyName) => {
    images.forEach((image) => {
      const active = image.dataset[keyName] === key;
      image.classList.toggle('is-active', active);
      image.setAttribute('aria-hidden', active ? 'false' : 'true');
    });
  };

  const revealActiveOption = (button) => {
    if (!window.matchMedia('(max-width: 820px)').matches) return;

    const rail = button.closest('[role="tablist"]');
    if (!rail || rail.scrollWidth <= rail.clientWidth + 8) return;

    const item = button.parentElement?.parentElement === rail ? button.parentElement : button;
    const firstItem = rail.firstElementChild;
    const left = item.offsetLeft - (firstItem?.offsetLeft || 0);
    rail.scrollTo({ left, behavior: reduceMotion ? 'auto' : 'smooth' });
  };

  const setupSwipeActivation = (rail, buttons, activate) => {
    if (!rail || buttons.length < 2) return;

    const mobileViewport = window.matchMedia('(max-width: 820px)');
    const railItem = (button) => (
      button.parentElement?.parentElement === rail ? button.parentElement : button
    );
    let settleTimer = null;

    const syncToNearestOption = () => {
      if (!mobileViewport.matches || rail.scrollWidth <= rail.clientWidth + 8) return;

      const items = buttons.map(railItem);
      const firstOffset = items[0]?.offsetLeft || 0;
      const nearestIndex = items.reduce((nearest, item, index) => {
        const distance = Math.abs((item.offsetLeft - firstOffset) - rail.scrollLeft);
        return distance < nearest.distance ? { index, distance } : nearest;
      }, { index: 0, distance: Number.POSITIVE_INFINITY }).index;

      if (!buttons[nearestIndex].classList.contains('is-active')) {
        activate(buttons[nearestIndex]);
      }
    };

    rail.addEventListener('scroll', () => {
      if (!mobileViewport.matches) return;
      window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(syncToNearestOption, 120);
    }, { passive: true });
  };

  const setupAutoRotation = (switcher, items, activate, delay = 5600) => {
    const toggle = switcher.querySelector('[data-carousel-toggle]');
    const root = switcher.closest('[data-ik-root]');
    if (reduceMotion || items.length < 2) {
      if (toggle) toggle.hidden = true;
      return () => {};
    }

    const icon = toggle?.querySelector('[data-carousel-icon]');
    let timer = null;
    let inView = !('IntersectionObserver' in window);
    let hovered = false;
    let focused = false;
    let userPaused = false;

    const updateToggle = () => {
      if (!toggle) return;
      toggle.setAttribute(
        'aria-label',
        userPaused ? (root?.dataset.labelPlayCarousel || 'Play carousel') : (root?.dataset.labelPauseCarousel || 'Pause carousel'),
      );
      if (icon) icon.className = userPaused ? 'ri-play-mini-fill' : 'ri-pause-mini-fill';
    };

    const stop = () => {
      if (timer === null) return;
      window.clearTimeout(timer);
      timer = null;
    };

    const canRotate = () => inView && !hovered && !focused && !userPaused && !document.hidden;

    const schedule = () => {
      stop();
      if (!canRotate()) return;
      timer = window.setTimeout(() => {
        if (!switcher.isConnected) {
          stop();
          return;
        }
        const currentIndex = Math.max(0, items.findIndex((item) => item.classList.contains('is-active')));
        activate(items[(currentIndex + 1) % items.length]);
        schedule();
      }, delay);
    };

    switcher.addEventListener('pointerenter', () => {
      hovered = true;
      stop();
    });
    switcher.addEventListener('pointerleave', () => {
      hovered = false;
      schedule();
    });
    switcher.addEventListener('focusin', () => {
      focused = true;
      stop();
    });
    switcher.addEventListener('focusout', () => {
      window.requestAnimationFrame(() => {
        focused = switcher.contains(document.activeElement);
        schedule();
      });
    });

    toggle?.addEventListener('click', () => {
      userPaused = !userPaused;
      updateToggle();
      schedule();
    });

    document.addEventListener('visibilitychange', schedule);

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(([entry]) => {
        inView = entry.isIntersecting;
        schedule();
      }, { threshold: 0.28 });
      observer.observe(switcher);
    } else {
      schedule();
    }

    updateToggle();
    return schedule;
  };

  const setupLoader = (root) => {
    const loader = root.querySelector('[data-ik-loader]');
    if (!loader) return;

    const video = root.querySelector('video.ik-hero__film');
    const minimumDisplayTime = reduceMotion ? 0 : 1750;
    const startedAt = window.performance.now();
    let dismissed = false;

    const dismiss = () => {
      if (dismissed) return;
      dismissed = true;

      const elapsed = window.performance.now() - startedAt;
      window.setTimeout(() => {
        loader.classList.add('is-hidden');
        loader.setAttribute('aria-hidden', 'true');
      }, Math.max(0, minimumDisplayTime - elapsed));
    };

    if (!video || video.readyState >= 2) {
      dismiss();
      return;
    }

    video.addEventListener('loadeddata', dismiss, { once: true });
    video.addEventListener('error', dismiss, { once: true });
    window.addEventListener('load', () => {
      if (video.readyState >= 2) dismiss();
    }, { once: true });
    window.setTimeout(dismiss, 4500);
  };

  const setupHeader = (root) => {
    const header = root.querySelector('[data-header]');
    const progress = root.querySelector('[data-page-progress]');
    const heroFilm = root.querySelector('.ik-hero__film');
    let frameRequested = false;

    const update = () => {
      frameRequested = false;
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const scrollRange = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);

      header?.classList.toggle('is-scrolled', scrollTop > 24);
      progress?.style.setProperty('--ik-page-progress', Math.min(1, scrollTop / scrollRange).toFixed(4));

      if (heroFilm && !reduceMotion && scrollTop < window.innerHeight * 1.15) {
        heroFilm.style.transform = `translate3d(0, ${Math.min(7, scrollTop * 0.008)}rem, 0) scale(1.04)`;
      }
    };

    const requestUpdate = () => {
      if (frameRequested) return;
      frameRequested = true;
      window.requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate, { passive: true });
  };

  const setupMenu = (root) => {
    const button = root.querySelector('[data-menu-button]');
    const menu = root.querySelector('[data-menu]');
    if (!button || !menu) return;

    const setOpen = (open) => {
      button.setAttribute('aria-expanded', String(open));
      button.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
      menu.classList.toggle('is-open', open);
      menu.setAttribute('aria-hidden', String(!open));
      document.body.classList.toggle('ik-menu-open', open);
    };

    button.addEventListener('click', () => {
      setOpen(button.getAttribute('aria-expanded') !== 'true');
    });

    menu.querySelectorAll('[data-menu-link]').forEach((link) => {
      link.addEventListener('click', () => setOpen(false));
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && button.getAttribute('aria-expanded') === 'true') {
        setOpen(false);
        button.focus();
      }
    });
  };

  const setupReveals = (root) => {
    const items = [...root.querySelectorAll('.ik-reveal')];
    if (!items.length || reduceMotion || !('IntersectionObserver' in window)) {
      items.forEach((item) => item.classList.add('is-visible'));
      return;
    }

    root.classList.add('ik-motion-ready');
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });

    items.forEach((item) => observer.observe(item));
  };

  const setupAutoScrollCarousels = (root) => {
    if (reduceMotion) return;

    root.querySelectorAll('[data-auto-scroll-carousel]').forEach((carousel) => {
      const items = [...carousel.children];
      if (items.length < 2) return;

      let timer = null;
      let inView = !('IntersectionObserver' in window);
      let interacting = false;

      const stop = () => {
        if (timer === null) return;
        window.clearTimeout(timer);
        timer = null;
      };

      const canScroll = () => carousel.scrollWidth > carousel.clientWidth + 8;

      const schedule = (delay = 2800) => {
        stop();
        if (!inView || interacting || document.hidden || !canScroll()) return;
        timer = window.setTimeout(() => {
          const visibleItems = items.filter((item) => item.offsetParent !== null);
          if (visibleItems.length < 2) return;

          const baseOffset = visibleItems[0].offsetLeft;
          const current = visibleItems.reduce((closest, item, index) => {
            const distance = Math.abs((item.offsetLeft - baseOffset) - carousel.scrollLeft);
            return distance < closest.distance ? { index, distance } : closest;
          }, { index: 0, distance: Number.POSITIVE_INFINITY }).index;
          const next = visibleItems[(current + 1) % visibleItems.length];

          carousel.scrollTo({
            left: next.offsetLeft - baseOffset,
            behavior: 'smooth',
          });
          schedule();
        }, delay);
      };

      carousel.addEventListener('pointerdown', () => {
        interacting = true;
        stop();
      });
      carousel.addEventListener('pointerup', () => {
        interacting = false;
        schedule(6500);
      });
      carousel.addEventListener('pointercancel', () => {
        interacting = false;
        schedule(6500);
      });
      carousel.addEventListener('mouseenter', () => {
        interacting = true;
        stop();
      });
      carousel.addEventListener('mouseleave', () => {
        interacting = false;
        schedule();
      });
      carousel.addEventListener('focusin', () => {
        interacting = true;
        stop();
      });
      carousel.addEventListener('focusout', () => {
        window.requestAnimationFrame(() => {
          interacting = carousel.contains(document.activeElement);
          schedule();
        });
      });

      document.addEventListener('visibilitychange', () => schedule());
      window.addEventListener('resize', () => schedule(), { passive: true });

      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver(([entry]) => {
          inView = entry.isIntersecting;
          schedule();
        }, { threshold: 0.35 });
        observer.observe(carousel);
      } else {
        schedule();
      }
    });
  };

  const setupSwitcherPreload = (root) => {
    const switchers = [...root.querySelectorAll('[data-app-switcher], [data-create-switcher], [data-display-switcher], [data-power-switcher]')];
    if (!switchers.length) return;

    const preload = (switcher) => {
      switcher.querySelectorAll('img').forEach((image) => {
        const source = image.currentSrc || image.src;
        if (!source) return;
        const loader = new Image();
        loader.decoding = 'async';
        loader.src = source;
      });
    };

    if (!('IntersectionObserver' in window)) {
      switchers.forEach(preload);
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        preload(entry.target);
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '900px 0px', threshold: 0.01 });

    switchers.forEach((switcher) => observer.observe(switcher));
  };

  const setupHomeSwitcher = (root) => {
    const switcher = root.querySelector('[data-home-switcher]');
    if (!switcher) return;

    const buttons = [...switcher.querySelectorAll('[data-home-option]')];
    const images = [...switcher.querySelectorAll('[data-home-image]')];
    const count = switcher.querySelector('[data-home-count]');
    const title = switcher.querySelector('[data-home-title]');
    const copy = switcher.querySelector('[data-home-copy]');

    const activate = (button) => {
      const key = button.dataset.homeOption;
      buttons.forEach((item) => {
        const active = item === button;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-selected', String(active));
        item.tabIndex = active ? 0 : -1;
      });
      setActiveImage(images, key, 'homeImage');
      if (count) count.textContent = button.dataset.count || '';
      if (title) title.textContent = button.dataset.title || '';
      if (copy) copy.textContent = button.dataset.copy || '';
    };

    buttons.forEach((button, index) => {
      button.addEventListener('click', () => activate(button));
      button.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        const direction = event.key === 'ArrowRight' ? 1 : -1;
        const next = buttons[(index + direction + buttons.length) % buttons.length];
        activate(next);
        next.focus();
      });
    });
  };

  const setupCreateSwitcher = (root) => {
    const switcher = root.querySelector('[data-create-switcher]');
    if (!switcher) return;

    const buttons = [...switcher.querySelectorAll('[data-create-option]')];
    const images = [...switcher.querySelectorAll('[data-create-image]')];
    const prompt = switcher.querySelector('[data-create-prompt]');
    let restartAutoRotation = () => {};

    const activate = (button, restart = false) => {
      const key = button.dataset.createOption;
      buttons.forEach((item) => {
        const active = item === button;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-selected', String(active));
        item.tabIndex = active ? 0 : -1;
      });
      setActiveImage(images, key, 'createImage');
      revealActiveOption(button);
      if (prompt) prompt.textContent = button.dataset.prompt || '';
      if (restart) restartAutoRotation();
    };

    buttons.forEach((button, index) => {
      button.addEventListener('click', () => activate(button, true));
      button.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'mouse') activate(button);
      });
      button.addEventListener('keydown', (event) => {
        if (!['ArrowUp', 'ArrowDown'].includes(event.key)) return;
        event.preventDefault();
        const direction = event.key === 'ArrowDown' ? 1 : -1;
        const next = buttons[(index + direction + buttons.length) % buttons.length];
        activate(next);
        next.focus();
      });
    });

    setupSwipeActivation(
      switcher.querySelector('.ik-create__options'),
      buttons,
      (button) => activate(button, true)
    );
    restartAutoRotation = setupAutoRotation(switcher, buttons, activate, 3600);
  };

  const setupDisplaySwitcher = (root) => {
    const switcher = root.querySelector('[data-display-switcher]');
    if (!switcher) return;

    const buttons = [...switcher.querySelectorAll('[data-display-option]')];
    const images = [...switcher.querySelectorAll('[data-display-image]')];
    const count = switcher.querySelector('[data-display-count]');
    const caption = switcher.querySelector('[data-display-caption]');
    let restartAutoRotation = () => {};

    const activate = (button, restart = false) => {
      const key = button.dataset.displayOption;
      buttons.forEach((item) => {
        const active = item === button;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-selected', String(active));
        item.tabIndex = active ? 0 : -1;
      });
      setActiveImage(images, key, 'displayImage');
      revealActiveOption(button);
      if (count) count.textContent = button.dataset.count || '';
      if (caption) caption.textContent = button.dataset.caption || '';
      if (restart) restartAutoRotation();
    };

    buttons.forEach((button, index) => {
      button.addEventListener('click', () => activate(button, true));
      button.addEventListener('keydown', (event) => {
        if (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        const direction = ['ArrowDown', 'ArrowRight'].includes(event.key) ? 1 : -1;
        const next = buttons[(index + direction + buttons.length) % buttons.length];
        activate(next, true);
        next.focus();
      });
    });

    activate(buttons.find((button) => button.classList.contains('is-active')) || buttons[0]);
    restartAutoRotation = setupAutoRotation(switcher, buttons, activate, 1500);
  };

  const setupPowerSwitcher = (root) => {
    const switcher = root.querySelector('[data-power-switcher]');
    if (!switcher) return;

    const buttons = [...switcher.querySelectorAll('[data-power-option]')];
    const images = [...switcher.querySelectorAll('[data-power-image]')];
    let restartAutoRotation = () => {};

    const activate = (button, restart = false) => {
      const key = button.dataset.powerOption;
      buttons.forEach((item) => {
        const active = item === button;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-selected', String(active));
        item.tabIndex = active ? 0 : -1;
      });
      setActiveImage(images, key, 'powerImage');
      revealActiveOption(button);
      if (restart) restartAutoRotation();
    };

    buttons.forEach((button, index) => {
      button.addEventListener('click', () => activate(button, true));
      button.addEventListener('keydown', (event) => {
        if (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        const direction = ['ArrowDown', 'ArrowRight'].includes(event.key) ? 1 : -1;
        const next = buttons[(index + direction + buttons.length) % buttons.length];
        activate(next, true);
        next.focus();
      });
    });

    activate(buttons.find((button) => button.classList.contains('is-active')) || buttons[0]);
    setupSwipeActivation(
      switcher.querySelector('ul[role="tablist"]'),
      buttons,
      (button) => activate(button, true)
    );
    restartAutoRotation = setupAutoRotation(switcher, buttons, activate, 3600);
  };

  const setupAppSwitcher = (root) => {
    const switcher = root.querySelector('[data-app-switcher]');
    if (!switcher) return;

    const buttons = [...switcher.querySelectorAll('[data-app-option]')];
    const images = [...switcher.querySelectorAll('[data-app-image]')];
    const count = switcher.querySelector('[data-app-count]');
    const title = switcher.querySelector('[data-app-title]');
    let restartAutoRotation = () => {};

    const activate = (button, restart = false) => {
      const key = button.dataset.appOption;
      buttons.forEach((item) => {
        const active = item === button;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-selected', String(active));
        item.tabIndex = active ? 0 : -1;
      });
      setActiveImage(images, key, 'appImage');
      revealActiveOption(button);
      if (count) count.textContent = button.dataset.count || '';
      if (title) title.textContent = button.dataset.title || '';
      if (restart) restartAutoRotation();
    };

    buttons.forEach((button, index) => {
      button.addEventListener('click', () => activate(button, true));
      button.addEventListener('keydown', (event) => {
        if (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        const direction = ['ArrowDown', 'ArrowRight'].includes(event.key) ? 1 : -1;
        const next = buttons[(index + direction + buttons.length) % buttons.length];
        activate(next, true);
        next.focus();
      });
    });

    restartAutoRotation = setupAutoRotation(switcher, buttons, activate, 2000);
  };

  const setupStyleSwitcher = (root) => {
    const switcher = root.querySelector('[data-style-switcher]');
    if (!switcher) return;

    const buttons = [...switcher.querySelectorAll('[data-style-option]')];
    const images = [...switcher.querySelectorAll('[data-style-image]')];
    const count = switcher.querySelector('[data-style-count]');
    const caption = switcher.querySelector('[data-style-caption]');

    const activate = (button) => {
      const key = button.dataset.styleOption;
      buttons.forEach((item) => {
        const active = item === button;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-selected', String(active));
        item.tabIndex = active ? 0 : -1;
      });
      setActiveImage(images, key, 'styleImage');
      if (count) count.textContent = button.dataset.count || '';
      if (caption) caption.textContent = button.dataset.caption || '';
    };

    buttons.forEach((button, index) => {
      button.addEventListener('click', () => activate(button));
      button.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'mouse') activate(button);
      });
      button.addEventListener('keydown', (event) => {
        if (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        const direction = ['ArrowDown', 'ArrowRight'].includes(event.key) ? 1 : -1;
        const next = buttons[(index + direction + buttons.length) % buttons.length];
        activate(next);
        next.focus();
      });
    });
  };

  const setupParallax = (root) => {
    const items = [...root.querySelectorAll('[data-parallax]')];
    if (!items.length || reduceMotion) return;

    let frameRequested = false;
    const update = () => {
      frameRequested = false;
      items.forEach((item) => {
        const rect = item.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top > window.innerHeight) return;
        const speed = Number.parseFloat(item.dataset.parallax || '0.05');
        const centerOffset = rect.top + rect.height / 2 - window.innerHeight / 2;
        const distance = Math.max(-42, Math.min(42, centerOffset * speed));
        item.style.setProperty('--ik-parallax-y', `${distance.toFixed(2)}px`);
      });
    };

    const requestUpdate = () => {
      if (frameRequested) return;
      frameRequested = true;
      window.requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate, { passive: true });
  };

  const setupFitLightbox = (root) => {
    const dialog = root.querySelector('[data-fit-lightbox]');
    const triggers = [...root.querySelectorAll('[data-fit-lightbox-trigger]')];
    if (!(dialog instanceof HTMLDialogElement) || !triggers.length) return;

    const image = dialog.querySelector('[data-fit-lightbox-image]');
    const inner = dialog.querySelector('.ik-fit-lightbox__inner');
    const closeButton = dialog.querySelector('[data-fit-lightbox-close]');
    const progress = dialog.querySelector('[data-fit-lightbox-progress]');
    const progressBar = dialog.querySelector('[data-fit-lightbox-progress-bar]');
    const progressLabel = dialog.querySelector('[data-fit-lightbox-progress-label]');
    const progressValue = dialog.querySelector('[data-fit-lightbox-progress-value]');
    let opener = null;
    let controller = null;
    let objectUrl = '';
    let requestId = 0;

    const releaseObjectUrl = () => {
      if (!objectUrl) return;
      URL.revokeObjectURL(objectUrl);
      objectUrl = '';
    };

    const loadingLabel = root.dataset.labelLoadingImage || 'Loading image';
    const readyLabel = root.dataset.labelImageReady || 'Image ready';
    const errorLabel = root.dataset.labelImageError || 'Unable to load image';

    const updateProgress = (value, label = loadingLabel) => {
      const percentage = Math.max(0, Math.min(100, Math.round(value)));
      progress?.classList.remove('is-indeterminate');
      if (progressBar) progressBar.style.width = `${percentage}%`;
      if (progressLabel) progressLabel.textContent = label;
      if (progressValue) progressValue.textContent = `${percentage}%`;
    };

    const setIndeterminate = () => {
      progress?.classList.add('is-indeterminate');
      if (progressBar) progressBar.style.removeProperty('width');
      if (progressLabel) progressLabel.textContent = loadingLabel;
      if (progressValue) progressValue.textContent = '';
    };

    const revealImage = (source, alt, activeRequest) => {
      if (!image) return;
      image.onload = () => {
        if (activeRequest !== requestId) return;
        updateProgress(100, readyLabel);
        window.requestAnimationFrame(() => dialog.classList.add('is-ready'));
      };
      image.onerror = () => {
        if (activeRequest !== requestId) return;
        progress?.classList.remove('is-indeterminate');
        if (progressBar) progressBar.style.width = '0%';
        if (progressLabel) progressLabel.textContent = errorLabel;
        if (progressValue) progressValue.textContent = '';
      };
      image.alt = alt;
      image.src = source;
    };

    const loadImage = async (trigger) => {
      if (!image) return;

      const source = trigger.dataset.fitSrc || '';
      const alt = trigger.dataset.fitAlt || '';
      const activeRequest = ++requestId;
      opener = trigger;

      controller?.abort();
      controller = 'AbortController' in window ? new AbortController() : null;
      releaseObjectUrl();
      image.onload = null;
      image.onerror = null;
      image.removeAttribute('src');
      image.alt = '';
      dialog.classList.remove('is-ready');
      updateProgress(0);

      if (!dialog.open) dialog.showModal();

      if (!source || !('fetch' in window) || !controller) {
        setIndeterminate();
        revealImage(source, alt, activeRequest);
        return;
      }

      try {
        const response = await fetch(source, {
          cache: 'force-cache',
          signal: controller.signal,
        });
        if (!response.ok || !response.body) throw new Error('Image stream unavailable');

        const total = Number.parseInt(response.headers.get('content-length') || '0', 10);
        if (!total) setIndeterminate();

        const reader = response.body.getReader();
        const chunks = [];
        let loaded = 0;

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          chunks.push(value);
          loaded += value.byteLength;
          if (total) updateProgress((loaded / total) * 100);
        }

        if (activeRequest !== requestId) return;
        const type = response.headers.get('content-type') || 'image/webp';
        objectUrl = URL.createObjectURL(new Blob(chunks, { type }));
        updateProgress(100, 'Preparing image');
        revealImage(objectUrl, alt, activeRequest);
      } catch (error) {
        if (error?.name === 'AbortError' || activeRequest !== requestId) return;
        setIndeterminate();
        revealImage(source, alt, activeRequest);
      }
    };

    const close = () => {
      controller?.abort();
      if (dialog.open) dialog.close();
    };

    triggers.forEach((trigger) => {
      trigger.addEventListener('click', () => loadImage(trigger));
    });

    closeButton?.addEventListener('click', close);
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog || event.target === inner) close();
    });
    dialog.addEventListener('close', () => {
      requestId += 1;
      controller?.abort();
      controller = null;
      if (image) {
        image.onload = null;
        image.onerror = null;
        image.removeAttribute('src');
        image.alt = '';
      }
      releaseObjectUrl();
      dialog.classList.remove('is-ready');
      updateProgress(0);
      opener?.focus();
      opener = null;
    });
  };

  const setupFaq = (root) => {
    const details = [...root.querySelectorAll('.ik-faq__list details')];
    details.forEach((item) => {
      item.addEventListener('toggle', () => {
        if (!item.open) return;
        details.forEach((other) => {
          if (other !== item) other.open = false;
        });
      });
    });
  };

  const setupSectionNavigation = (root) => {
    if (!('IntersectionObserver' in window)) return;
    const links = [...root.querySelectorAll('.ik-header__nav a[href^="#"]')];
    const sections = links
      .map((link) => root.querySelector(link.getAttribute('href')))
      .filter(Boolean);

    const observer = new IntersectionObserver((entries) => {
      const visible = entries
        .filter((entry) => entry.isIntersecting)
        .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
      if (!visible) return;

      links.forEach((link) => {
        link.classList.toggle('is-active', link.getAttribute('href') === `#${visible.target.id}`);
      });
    }, { rootMargin: '-35% 0px -55% 0px', threshold: [0, 0.1, 0.3] });

    sections.forEach((section) => observer.observe(section));
  };

  const setupVideoVisibility = (root) => {
    const video = root.querySelector('.ik-hero__film');
    if (!(video instanceof HTMLVideoElement)) return;

    if (reduceMotion) {
      video.pause();
      return;
    }

    if (!('IntersectionObserver' in window)) return;

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        const play = video.play();
        if (play && typeof play.catch === 'function') play.catch(() => {});
      } else {
        video.pause();
      }
    }, { threshold: 0.08 });

    observer.observe(video);
  };

  const init = (root) => {
    if (root.dataset.ikReady === 'true') return;
    root.dataset.ikReady = 'true';

    setupLoader(root);
    setupHeader(root);
    setupMenu(root);
    setupReveals(root);
    setupAutoScrollCarousels(root);
    setupSwitcherPreload(root);
    setupHomeSwitcher(root);
    setupDisplaySwitcher(root);
    setupPowerSwitcher(root);
    setupCreateSwitcher(root);
    setupAppSwitcher(root);
    setupStyleSwitcher(root);
    setupParallax(root);
    setupFitLightbox(root);
    setupFaq(root);
    setupSectionNavigation(root);
    setupVideoVisibility(root);
  };

  const initAll = () => {
    document.querySelectorAll('[data-ik-root]').forEach(init);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll, { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
})();
